import { create } from 'zustand';

/**
 * G-08 — "VIEW AS": THE OWNER LOOKING THROUGH ANOTHER PERSON'S EYES.
 *
 * A read-only preview, held in the BROWSER and nowhere else. The Owner picks
 * an active user on Admin › Users; the client fetches that person's effective
 * permission list (GET /api/users/:id/effective-permissions — Owner-only and
 * audit-logged) and, while this store holds a target, the permission layer
 * (`lib/permissions.ts`, `useMe`) answers from THAT list with `isOwner=false`,
 * so menus, gates and screens redraw as the target would see them.
 *
 * ── THE SERVER SESSION IS NEVER CHANGED ────────────────────────────────────
 *
 * Nothing here touches a request: no cookie, no header, no query parameter
 * names the previewed person, and every call the browser makes while
 * previewing is still the Owner's own, answered by the Owner's permissions.
 * That is exactly why writes are refused CLIENT-SIDE while the preview is on
 * (`apps/web/src/api.ts`): a save made "as Omar" would be a save made by the
 * Owner, and the banner would be lying. The preview changes what is DRAWN,
 * never what may be DONE — BR-PERM-50's sentence read from the other side.
 *
 * ── MEMORY + sessionStorage ─────────────────────────────────────────────────
 *
 * A refresh keeps the preview (the Owner is mid-check); a new tab does not
 * (sessionStorage is per tab), so a preview can never follow the Owner into
 * work they meant to do as themselves. Sign-out clears it.
 */
export interface ViewAsTarget {
  readonly userId: string;
  readonly name: string;
  readonly permissions: readonly string[];
}

const STORAGE_KEY = 'app.viewAs';

interface ViewAsState {
  target: ViewAsTarget | null;
  /** How many writes the client refused during this preview — the banner
   *  shows the sentence while it is above zero. */
  blocked: number;
  enter: (target: ViewAsTarget) => void;
  exit: () => void;
  noteBlocked: () => void;
}

function readPersisted(): ViewAsTarget | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as ViewAsTarget).userId === 'string' &&
      typeof (parsed as ViewAsTarget).name === 'string' &&
      Array.isArray((parsed as ViewAsTarget).permissions)
    ) {
      const { userId, name, permissions } = parsed as ViewAsTarget;
      return { userId, name, permissions: permissions.filter((p) => typeof p === 'string') };
    }
    return null;
  } catch {
    return null;
  }
}

function persist(target: ViewAsTarget | null): void {
  try {
    if (target === null) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, JSON.stringify(target));
  } catch {
    /* without storage the preview simply does not survive a refresh */
  }
}

export const useViewAsStore = create<ViewAsState>((set) => ({
  target: readPersisted(),
  blocked: 0,
  enter: (target) => {
    persist(target);
    set({ target, blocked: 0 });
  },
  exit: () => {
    persist(null);
    set({ target: null, blocked: 0 });
  },
  noteBlocked: () => set((state) => ({ blocked: state.blocked + 1 })),
}));

/** The active preview target, or null. A hook, for components. */
export function useViewAs(): ViewAsTarget | null {
  return useViewAsStore((state) => state.target);
}

/** The same answer outside React — the API client reads it per request. */
export function currentViewAs(): ViewAsTarget | null {
  return useViewAsStore.getState().target;
}

export function enterViewAs(target: ViewAsTarget): void {
  useViewAsStore.getState().enter(target);
}

export function exitViewAs(): void {
  useViewAsStore.getState().exit();
}

/** Called by the API client when it refuses a write during a preview. */
export function noteBlockedWrite(): void {
  useViewAsStore.getState().noteBlocked();
}
